<template>
  <div class="users">
    <div>
      <CustomerInfo v-if="unit" type="unit" :customerInfo="unit" :alarmCenters="alarm_centrals" @updateAddress="onUnitUpdated($event)" />
      <!-- Notes section (collapsible) -->
      <div class="w-full" v-if="(user.role == 'admin')">
        <div class="w-full px-4 py-3 cursor-pointer bg-primary-50 border border-primary-200 hover:bg-primary-100 transition-colors mt-3" :class="isNotesVisible ? 'rounded-t-lg border-b-0' : 'rounded-lg'" @click="isNotesVisible = !isNotesVisible">
          <div class="flex items-center justify-between">
            <div class="text-primary-700 font-bold text-base font-serif flex items-center">
              {{ $t('notes.notes_section') }}
              <span v-if="admin_notes" class="inline-block w-2.5 h-2.5 rounded-full bg-accent-400 ml-2"></span>
            </div>
            <div class="text-primary-500">
              <BaseIcon v-if="!isNotesVisible" icon="chevron-down" />
              <BaseIcon v-if="isNotesVisible" icon="chevron-up" />
            </div>
          </div>
        </div>
        <div v-if="isNotesVisible" class="px-4 pt-3 pb-4 bg-primary-50 border border-t-0 border-primary-200 rounded-b-lg">
          <textarea v-model="admin_notes" class="w-full h-36 p-4 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" :placeholder="$t('notes.admin_notes_placeholder')" :disabled="isDisabled"></textarea>
          <div class="flex items-center mt-2">
            <button class="btn-blue-outline mx-2" @click="toggleDisabled">
              {{ isDisabled ? $t('notes.unlock_notes') : $t('notes.lock_notes') }}
              <BaseIcon :icon="isDisabled ? 'lock' : 'lock-open'" class="ml-1"/>
            </button>
            <button class="btn-green-outline mx-2" v-if="(!this.isDisabled)" @click.prevent="saveAdminNotes()">
              {{ $t('notes.save') }}
              <BaseIcon icon="save" class="ml-1" />
            </button>
          </div>
        </div>
      </div>
      <!-- End Notes section -->
      <AlarmInfo :alarmInfo="alarm_info" :units="units" :customerId="customer_id" :id="id" :unitAlarms="alarms" :devicesTree="devicesTree" @updateAlarm="getAlarms()" @updateDeviceTree="getDevicesTree()" @replaceDevice="startReplaceDevice($event)" />
      <AlarmDetail :alarmInfo="alarm_info" :bulkAlarms="bulkAlarms" :alarmCentrals="alarm_centrals" :sellers="sellers" :customer="customer" :units="units" @updateAlarmContract="onAlarmContractUpdated()" />
      <!-- SEC-336: the action report for a replacement device -->
      <ReplaceDevice v-if="user.role === 'admin'" :alarmInfo="replace_alarm_info" :alarmCentrals="alarm_centrals" :sellers="sellers" :customer="customer" @deviceReplaced="onDeviceReplaced()" />
      <ErrorReport :report="errorReport" />
      <OrderAlarmModal :unit_id="unit_id" />
      <AdminOrderAlarmModal v-if="user.role === 'admin' || user.role === 'seller'" :unit_id="unit_id" :sellers="sellers" :alarm_centers="alarm_centrals" :customer="customer" :unit="unit" />
      <OrderDeviceDetailsModal v-if="user.role === 'admin'" :devices="incompleteOrderDevices" :unitName="unit ? unit.name : ''" @devices-updated="getAlarms()" />
      <!-- devices created from an order that still miss their unique fields (SEC-216) -->
      <div v-if="user.role === 'admin' && incompleteOrderDevices.length" class="flex items-center justify-between bg-yellow-100 text-yellow-800 text-sm rounded px-4 py-3 mt-3">
        <span>
          <BaseIcon icon="exclamation-triangle" class="mr-2" />
          {{ $t("order_devices.incomplete_banner", { count: incompleteOrderDevices.length }) }}
        </span>
        <button class="btn-blue px-3 py-1.5" @click="$modal.show('modal-order-device-details')">
          {{ $t("order_devices.incomplete_button") }}
        </button>
      </div>
      <vue-good-table
        styleClass="vgt-table bordered"
        row-style-class="text-sm"
        class="mt-12"
        ref="alarmTable"
        :columns="alarm_columns"
        :rows="alarms_rows"
        :search-options="{ enabled: false }"
        :pagination-options="{ enabled: false }"
        :sort-options="{
          enabled: true,
          initialSortBy: { field: 'unit_name', type: 'asc' },
        }"
        :select-options="{
          enabled: true,
          selectOnCheckboxOnly: true,
          disableSelectInfo: true,
          selectAllByGroup: false,
        }"
        :group-options="tableGroupOptions"
        @on-selected-rows-change="selectedAlarms = $event.selectedRows"
      >
        <div slot="table-actions">
          <div class="flex flex-wrap">
            <router-link class="btn-green-outline mx-2 mt-2 xl:mt-0" v-if="user.role == 'admin' || user.role == 'seller'" :to="'/admin/skapa-larm/' + customer_id + '/' + unit_id">
              {{ $t("departments_alarm.new_product") }}
              <BaseIcon icon="plus" class="ml-1" />
            </router-link>
            <button class="btn-green-outline mx-2 mt-2 xl:mt-0" @click.prevent="showAdminOrderModal()" v-if="user.role == 'admin' || user.role == 'seller'">
              {{ $t("departments_alarm.order") }}
              <BaseIcon icon="arrow-right" class="ml-1" />
            </button>
            <button class="btn-red-outline mx-2 mt-2 xl:mt-0" v-if="user.role == 'admin' && alarms && alarms.length > 0" @click.prevent="deleteAlarmsPrompt()">
              {{ $t("departments_alarm.delete") }}
              <BaseIcon icon="trash" class="ml-1" />
            </button>
            <router-link class="btn-secondary-outline mx-2 mt-2 xl:mt-0" v-if="user.role == 'admin' || user.role == 'seller'" :to="'/unit/floor-plan/' + customer_id + '/' + unit_id">
              {{ $t("departments_alarm.floor_plan") }}
              <BaseIcon icon="building" class="ml-1" />
            </router-link>
            <button class="btn-green-outline mx-2 mt-2 xl:mt-0" @click.prevent="showOrderModal()" v-if="user.role == 'superuser' || user.role == 'user'">
              {{ $t("departments_alarm.order_new_alarm") }}
              <BaseIcon icon="arrow-right" class="ml-1" />
            </button>
            <router-link class="btn-secondary-outline mx-2 mt-2 xl:mt-0" v-if="(user.role == 'admin' || user.role == 'seller' || (user.role == 'reseller' && user.reseller_special_access.indexOf('securtrack') > -1) || ((user.role == 'superuser' || user.role == 'user') && hasAddon('gps_geofence'))) && alarms && alarms.length > 0" :to="'/gps-unit/customer/' + unit_id + '/-'">
              {{ $t("departments_alarm.secur_track") }}
              <BaseIcon icon="map-marker-alt" class="ml-1" />
            </router-link>
            <router-link class="btn-secondary-outline mx-2 mt-2 xl:mt-0" v-if="(user.role == 'admin' || user.role == 'seller' || (user.role == 'reseller' && user.reseller_special_access.indexOf('securtool') > -1) || ((user.role == 'superuser' || user.role == 'user') && hasAddon('rounding_surveillance'))) && alarms && alarms.length > 0" :to="'/ronderingsbevakning/unit/' + unit_id">
              {{ $t("departments_alarm.secur_tool") }}
              <BaseIcon icon="history" class="ml-1" />
            </router-link>
            <router-link class="btn-secondary-outline mx-2 mt-2 xl:mt-0" v-if="(user.role == 'admin' || user.role == 'seller' || (user.role == 'reseller' && user.reseller_special_access.indexOf('test_reminder') > -1)) && alarms && alarms.length > 0" :to="'/alarm-reminder/unit/' + unit_id">
              {{ $t("departments_alarm.test_alarm") }}
              <BaseIcon icon="bell" class="ml-1" />
            </router-link>
            <button class="btn-blue-outline mx-2 mt-2 xl:mt-0" @click.prevent="exportAlarms()">
              {{ $t("export") }}
              <BaseIcon icon="file-excel" class="ml-1" />
            </button>
          
            <button class="btn-green mx-2 mt-2 xl:mt-0" v-if="user.role == 'admin' && eligibleSelectedAlarms.length > 0" @click.prevent="startBulkUpdate()">
              {{ $t("bulk_alarm_update.button", { count: eligibleSelectedAlarms.length }) }}
              <BaseIcon icon="edit" class="ml-1" />
            </button>
          </div>
        </div>
        <template slot="table-header-row" slot-scope="props">
          <span class="ml-5">
            {{ props.row.label }}
          </span>
        </template>
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field == 'lastSeen'">
            <span v-if="!new RegExp('(SRT334)|(TWIG Beacon)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)|(SecurDesktop)').test(props.row.type)">
              <span v-if="props.row.lastSeen">
                <span v-if="new RegExp('^SRT').test(props.row.type)" class="last-seen" v-bind:class="{ inactive: props.row.lastSeenTime > 1000 * 60 * 60 }">⬤</span>
                <span v-if="new RegExp('^TWIG').test(props.row.type)" class="last-seen" v-bind:class="{ inactive: props.row.lastSeenTime > 1000 * 60 * 60 * 2.5 }">⬤</span>
                <span v-if="new RegExp('^EMERIT').test(props.row.type) || props.row.type === 'securapp_eb10'" class="last-seen" v-bind:class="{ inactive: props.row.lastSeenTime > 1000 * 60 * 60 }">⬤</span>
                <span v-if="new RegExp('^TELTONIKA').test(props.row.type)" class="last-seen" v-bind:class="{ inactive: props.row.lastSeenTime > 1000 * 60 * 60 }">⬤</span>
              </span>
              <span v-else>
                <span class="last-seen grey">⬤</span>
              </span>
            </span>
            <span v-else-if="new RegExp('(SRT334)|(TWIG Beacon)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(props.row.type)">
              <!-- todo: better last seen time for ajax devices, since they are updated every X hours -->
              <span v-if="props.row.lastSeen">
                <span class="last-seen" v-bind:class="{ inactive: props.row.tagLastSeen > 56, orange: props.row.tagLastSeen > 28 && props.row.tagLastSeen <= 56 }">⬤</span>
              </span>
              <span v-else-if="new RegExp('(Avigilon Alta Compact Dome 5MP)').test(props.row.type)">
                <BaseIcon icon="camera" class="ml-1" />
              </span>
              <span v-else>
                <span class="last-seen grey">⬤</span>
              
              </span>
            </span>
            <span v-else-if="/(4G Box)/.test(props.row.type)">
              <span v-if="props.row.lastSeen">
                <span class="last-seen" v-bind:class="{ inactive: props.row.tagLastSeen > 7, orange: props.row.tagLastSeen > 2 && props.row.tagLastSeen <= 7 }">⬤</span>
              </span>
              <span v-else>
                <span class="last-seen grey">⬤</span>
              </span>
            </span>
          </span>
          <span v-else-if="props.column.field == 'agreement_number'">
            <!-- The number is coloured only where the agreement's insurance
                 state is actually known: green = insured, red = no insurance.
                 An agreement with no insurance recorded (and one whose insurance
                 this view was never told about) is shown as a plain number -
                 "not recorded" is not the same as "not insured", so it must not
                 read as red. Same rule as the INS badge in the agreements view. -->
            <template v-if="props.row.agreement_number">
              <span
                v-if="agreementInsuranceState(props.row.agreement_number) !== null"
                class="inline-block px-2 py-0.5 text-xs font-semibold rounded"
                :class="agreementInsuranceState(props.row.agreement_number) ? 'text-green-700 bg-green-100' : 'text-red-700 bg-red-100'"
              >
                {{ props.row.agreement_number }}
              </span>
              <span v-else>{{ props.row.agreement_number }}</span>
            </template>
          </span>
          <span v-else-if="props.column.field == 'device_status'">
            <span v-if="props.row.device_status" class="text-xs font-semibold px-2 py-0.5 rounded inline-block">{{ getDeviceStatusLabel(props.row.device_status) }}</span>
          </span>
          <span v-else-if="props.column.field == 'sellerName' && /^admin|^seller/.test(user.role)" class="text-xxs">
            {{ getSellerName(props.row.seller) }}
          </span>
          <span v-else-if="props.column.field == 'errorReport'">
            <button class="btn-red-outline opacity-75 hover:opacity-100" @click.prevent="showErrorReport(props.row)">{{ $t("departments_alarm.error_reporting") }}</button>
          </span>
          <span v-else-if="props.column.field == 'viewAlarm'">
            <button class="btn-blue-outline" @click.prevent="showAlarmInfoModal(props.row)">{{ $t("view") }}</button>
          </span>
          <span v-else-if="props.column.field == 'downloadPdf' && !/(^SRT330$)|(^SRT430$)|(^SRT334$)|(^TWIG Beacon$)|(^4G Box$)|(^TELTONIKA)|(^Anchor Beacon)|(^Avigilon Alta Compact Dome 5MP)|(^AJAX)/.test(props.row.type)">
            <span @click="downloadPDF(props.row)"><BaseIcon icon="file-pdf" class="text-red-600 text-2xl cursor-pointer"/></span>
          </span>
          <span v-else>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>
        <div slot="emptystate" class="flex flex-wrap flex-col items-center my-5">
          <div class="text-sm mb-4">{{ $t("departments_alarm.no_alarm_message") }}</div>
          <div v-if="user.role == 'superuser' || user.role == 'user'">
            <button class="btn-blue px-5 py-4 max-w-sm" @click.prevent="showOrderModal()">
              {{ $t("departments_alarm.order_new_alarm") }}
              <BaseIcon icon="arrow-right" class="ml-1" />
            </button>
          </div>
        </div>
      </vue-good-table>
    </div>
  </div>
</template>

<script>
import CustomerInfo from "@/components/customer_info";
import AlarmInfo from "@/components/modal/alarm_info";
import AlarmDetail from "@/components/modal/alarm_detail";
import ReplaceDevice from "@/components/modal/replace_device";
import ErrorReport from "@/components/modal/error_report";
import OrderAlarmModal from "@/components/modal/order_alarm";
import AdminOrderAlarmModal from "@/components/modal/admin_order_alarm";
import OrderDeviceDetailsModal from "@/components/modal/automatic_device_creation_details";
import { isOrderDeviceIncomplete } from "@/utils/automaticDeviceCreationFields";
import * as siteContext from "@/utils/siteContext";
import { isWhiteLabelCustomer } from "@/utils/brand";

// Device types that carry no alarm documentation: the plan sections are hidden
// for them in the editor and they are excluded from PDF send. They can never
// take part in a bulk documentation update.
const NO_DOCUMENTATION_TYPES = /(SRT334)|(SRT330)|(SRT430)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)|(SecurDesktop)/;

// SEC-327: alarms created from an order are named "<type> <number>" —
// "SRT430 1", "SRT430 2", ... "SRT430 10". Sorting those as plain text puts
// "SRT430 10" between "1" and "2", because it compares the characters "1" and
// "0" instead of the number ten. Support then has to hunt for each unit, and a
// missing device in the middle of a delivery is easy to overlook.
//
// These two helpers compare digit groups as numbers and everything else as
// text, so an order is listed as the numbered series it was registered as.

// Break a name into pieces: digit groups become numbers, the rest stays text.
// "SRT430 10" becomes ["SRT", 430, " ", 10].
function splitNameIntoParts(text) {
  const parts = [];
  const pieces = String(text == null ? "" : text).match(/\d+|\D+/g);

  if (!pieces) return parts;

  for (const piece of pieces) {
    if (/^\d+$/.test(piece)) {
      parts.push(Number(piece));
    } else {
      parts.push(piece);
    }
  }

  return parts;
}

// Compare two alarm names. Returns -1, 0 or 1.
function compareAlarmNames(a, b) {
  const left = splitNameIntoParts(a);
  const right = splitNameIntoParts(b);
  const shortest = Math.min(left.length, right.length);

  for (let i = 0; i < shortest; i++) {
    const x = left[i];
    const y = right[i];

    if (x === y) continue;

    // Two numbers: compare them as numbers, so 2 comes before 10.
    if (typeof x === "number" && typeof y === "number") {
      return x < y ? -1 : 1;
    }

    // Anything else: compare as text, so names without numbers still sort
    // alphabetically.
    const result = String(x).localeCompare(String(y), "sv", { sensitivity: "base" });
    if (result !== 0) return result < 0 ? -1 : 1;
  }

  // One name is the start of the other ("SRT430" and "SRT430 2"): the shorter
  // one comes first.
  if (left.length !== right.length) return left.length < right.length ? -1 : 1;

  return 0;
}

export default {
  title() {
    return this.$t("page_titles.alarm_view");
  },
  components: {
    CustomerInfo,
    AlarmInfo,
    AlarmDetail,
    ReplaceDevice,
    ErrorReport,
    OrderAlarmModal,
    AdminOrderAlarmModal,
    OrderDeviceDetailsModal,
  },
  data() {
    return {
      type: this.$route.params.type,
      customer_id: this.$route.params.customer_id,
      unit_id: this.$route.params.unit_id,
      id: this.$route.params.id,
      selected_alarm_id: this.$route.params.alarmId,
      modalShown: false,
      tableGroupOptions: {
        enabled: true,
        collapsable: true,
      },
      alarms: [],
      alarms_rows: [],
      unit: null,
      units: null,
      devicesTree: [],
      customer: null,
      alarm_centrals: [],
      sellers: [],
      errorReport: null,
      alarm_info: null,
      // SEC-336: the device being replaced. Kept apart from alarm_info so the
      // replace form is only filled in once the flow actually starts.
      replace_alarm_info: null,
      isDisabled: true,
      isNotesVisible: false,
      admin_notes: "",
      admin_device_notes: "",
      reseller_device_notes: "",
      agreementInsuranceMap: {},
      selectedAlarms: [],
      bulkAlarms: null,
    };
  },

  computed: {
    user() {
      return this.$store.state.user;
    },
    incompleteOrderDevices() {
      return this.alarms.filter((alarm) => isOrderDeviceIncomplete(alarm));
    },
    // selected devices that actually carry alarm documentation (REQ-01)
    eligibleSelectedAlarms() {
      return this.selectedAlarms.filter((alarm) => !NO_DOCUMENTATION_TYPES.test(alarm.type));
    },
    // SEC-334: customer of a white label reseller, see utils/brand.isWhiteLabelCustomer
    whiteLabelCustomer() {
      return isWhiteLabelCustomer(this.user);
    },
    alarm_columns() {
      return [
        { label: this.$t("departments_alarm_table.alarm_type"), field: "type", sortable: true, thClass: "text-sm" },
        { label: this.$t("departments_alarm_table.product_name"), field: "unit_name", sortable: true, thClass: "text-sm", sortFn: this.sortAlarmName },
        { label: this.$t("departments_alarm_table.phone_number"), field: "phone", sortable: true, thClass: "text-sm" },
        { label: this.$t("departments_alarm_table.serial_number"), field: "modelnumber", sortable: true, thClass: "text-sm" },
        { label: this.$t("departments_alarm_table.status"), field: "lastSeen", sortable: false, thClass: "text-sm" },
        // SEC-334: a white label reseller keeps its own agreements, so its customers see no agreement number
        ...(this.whiteLabelCustomer ? [] : [{ label: this.$t("departments_alarm_table.agreement_number") || 'Avtalsnr', field: "agreement_number", sortable: true, thClass: "text-sm" }]),
        ...(/^admin|^seller/.test(this.user.role) ? [{ label: this.$t('device_status.label'), field: 'device_status', sortable: false, thClass: 'text-sm' }] : []),
        { label: "", field: "sellerName", sortable: false },
        { label: "", field: "errorReport", sortable: false, thClass: "text-sm" },
        { label: "", field: "viewAlarm", sortable: false, thClass: "text-sm" },
        { label: "", field: "downloadPdf", sortable: false, thClass: "text-sm" },
      ];
    },
  },

  methods: {
    // SEC-327: order the alarm name column the way an order is numbered, so
    // "SRT430 10" comes after "SRT430 9" instead of right after "SRT430 1".
    sortAlarmName(xName, yName, column, xRow, yRow) {
      const byName = compareAlarmNames(xName, yName);
      if (byName !== 0) return byName;

      // Two devices can end up with the same name after a rename. Fall back
      // to the record id so the list keeps the same order on every reload.
      const xId = xRow && xRow._id ? String(xRow._id) : "";
      const yId = yRow && yRow._id ? String(yRow._id) : "";
      if (xId === yId) return 0;
      return xId < yId ? -1 : 1;
    },

    getAlarms() {
      var vm = this;
      let apiUrl = "";

      if (this.type == "customer" && (this.user.role == "superuser" || this.user.role == "user" || this.user.role == "admin" || this.user.role == "seller")) {
        apiUrl = `${process.env.VUE_APP_SERVER_URL}/unit/alarms/${this.unit_id}`;
      } else if (this.type == "reseller" && (this.user.role == "reseller" || this.user.role == "admin")) {
        apiUrl = `${process.env.VUE_APP_SERVER_URL}/reseller/${this.id}/unit/alarms/${this.unit_id}`;
      } else {
        this.alarms = [];
        return;
      }

      this.axios
        .get(apiUrl)
        .then((response) => {
          var larms = [],
            otherProducts = [];
          for (let i = 0; i < response.data.unit_alarms.length; i++) {
            let el = response.data.unit_alarms[i];
            let a = _.find(response.data.gps_alarms, { imei_number: el.imei_number });

            if (el.gps_active && a) {
              let lastSeenObj = this.getAlarmLastSeenTime(a);
              el.lastSeen = lastSeenObj.lastSeen;
              el.lastSeenTime = lastSeenObj.lastSeenTime;
            }
            // tag status
            if (/(^SRT334$)|(^TWIG Beacon$)|(^4G Box$)|(^Anchor Beacon$)|(^Avigilon Alta Compact Dome 5MP)|(^AJAX)|(^SecurDesktop)/.test(el.type) && el.last_rftag_activity) {
              el.lastSeen = this.moment(el.last_rftag_activity).fromNow();
              el.tagLastSeen = this.moment().diff(this.moment(el.last_rftag_activity), "days");
            }

            if (/(^SRT334$)|(^TWIG Beacon$)|(^4G Box$)|(^Anchor Beacon$)|(^Avigilon Alta Compact Dome 5MP)|(^AJAX)|(^SecurDesktop)/.test(el.type)) otherProducts.push(el);
            else larms.push(el);
          }
          this.alarms = [...larms, ...otherProducts];

          if (this.alarms.length == 0) {
            this.tableGroupOptions = {
              enabled: false,
              collapsable: false,
            };
            this.alarms_rows = [];
          } else {
            this.tableGroupOptions = {
              enabled: true,
              collapsable: true,
            };

            this.alarms_rows = [
              {
                mode: "span",
                label: "Larms",
                children: larms,
              },
              {
                mode: "span",
                label: "Andra produkter",
                children: otherProducts,
              },
            ];

            this.$nextTick().then(() => {
              vm.$refs["alarmTable"].expandAll();
            });
          }

          if (this.selected_alarm_id && !this.modalShown) {
            let a = _.find(this.alarms, { _id: this.selected_alarm_id });
            if (a) this.showAlarmInfoModal(a);
          }

          // checkboxes intentionally start unchecked (REQ-04, SEC-218) -
          // devices are only ever acted on after an active selection
          this.selectedAlarms = [];
          this.modalShown = true;
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    getCustomer() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/customer/${this.customer_id}`)
        .then((response) => {
          if (response.data.customer) this.customer = response.data.customer;

          this.units = response.data.units;
          this.findUnit();
          if (this.unit) this.setPageTitle(`${this.unit.name} - ${this.$t("top_titles.alarm_view")}`, "alarms");
          this.admin_notes = this.unit ? this.unit.admin_notes : "";
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    getDevicesTree() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/list/customer/${this.customer_id}/devices`)
        .then((response) => {
          this.devicesTree = response.data.devicesTree;
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    getAlarmCentrals() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/alarm-central`)
        .then((response) => {
          this.alarm_centrals = response.data.data;
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    getSellersResellers() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/seller/reseller`)
        .then((response) => {
          this.sellers = response.data.data;
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    findUnit() {
      let u = _.find(this.units, { unit_id: this.unit_id });
      if (u) this.unit = u;
    },

    // The edited business, as saved. Also replaced in the list, which the
    // alarm plan PDF reads (customer-specific instruction).
    onUnitUpdated(updated) {
      this.unit = updated;
      const idx = _.findIndex(this.units, { _id: updated._id });
      if (idx >= 0) this.units.splice(idx, 1, updated);
    },

    hasAddon(addonName) {
      if (this.unit?.addons && this.unit.addons.indexOf(addonName) >= 0) return true;
      if (this.customer?.addons && this.customer.addons.indexOf(addonName) >= 0) return true;
      return false;
    },

    showAlarmInfoModal(alarm) {
      this.bulkAlarms = null; // single-device flow must never run in bulk mode
      this.alarm_info = _.cloneDeep(alarm);
      this.show("update-alarm");
    },

    // SEC-336: the admin answered "Ja" to the replace question in the device
    // window. Open the new action report, prefilled from the device being
    // replaced. Nothing is saved until that report is submitted.
    startReplaceDevice(alarm) {
      if (this.user.role !== "admin") return; // same rule as the server route
      this.replace_alarm_info = _.cloneDeep(alarm);
      this.show("replace-device");
    },

    // The replacement was saved. Both devices changed and the old one has left
    // this department, so reload the list and the on-screen message tree -
    // that tree is built from the devices in the department.
    onDeviceReplaced() {
      this.replace_alarm_info = null;
      this.getAlarms();
      this.getDevicesTree();
    },

    // Called when the user clicks "Bulk update X devices". The empty-field
    // bulk editor (SEC-218 v2) opens directly for all selected eligible
    // devices - only the fields support fills in get written, so there is no
    // documentation comparison and no differing-devices warning.
    startBulkUpdate() {
      // demo feedback: admin only, both here and on the server route
      if (this.user.role !== "admin") return;
      // Keep only devices that carry alarm documentation (AJAX, SRT330,
      // TELTONIKA, beacons etc. are excluded from the count and the update).
      const eligibleDevices = this.eligibleSelectedAlarms;
      if (eligibleDevices.length === 0) return;

      this.openBulkEditor(eligibleDevices);
    },

    openBulkEditor(alarms) {
      // fresh clones so nothing leaks into the table rows. The bulk editor
      // starts empty and does not use a reference device.
      this.bulkAlarms = alarms.map((a) => _.cloneDeep(a));
      this.show("update-alarm-contract");
    },

    onAlarmContractUpdated() {
      this.bulkAlarms = null;
      this.getAlarms();
    },

    showErrorReport(alarm) {
      this.errorReport = {
        model: alarm.modelnumber,
        phone: alarm.phone,
        imei_number: alarm.imei_number,
        customer_name: this.customer.name,
        unit_name: this.getUnitName(alarm.parent_id),
        contact_phone: this.unit.contact_name,
        contact_email: this.user.email,
        message: "",
      };
      this.show("error-report");
    },

    exportAlarms() {
      // rows used to start all-selected; now that they start unchecked (REQ-04),
      // an empty selection keeps the old behavior and exports every device
      const selectedRows = this.$refs["alarmTable"].selectedRows;
      const exportRows = selectedRows.length > 0 ? selectedRows : this.alarms;
      const newColumns = exportRows.map((item) => {

        const row = {
          [this.$t("department_export.customer_name")]: this.customer.name,
          [this.$t("department_export.department")]: item.alarm_unit_name,
          [this.$t("department_export.device_type")]: item.type,
          [this.$t("department_export.device_name")]: item.unit_name,
          [this.$t("department_export.phone")]: item.phone,
          [this.$t("department_export.serial_number")]: item.modelnumber,
          // SEC-334: like the table, no agreement number for a white label reseller's customers
          ...(this.whiteLabelCustomer ? {} : { [this.$t("department_export.agreement_number")]: item.agreement_number || "" }),
          [this.$t("department_export.alarm_center")]: item.alarm_central,
        };
        if (this.user.role === "reseller") {
          row[this.$t("department_export.notes")] = item?.reseller_device_notes || "";
        }
        if (this.user.role === "admin") {
          row[this.$t("department_export.notes")] = item?.reseller_device_notes || "",
          row[this.$t("Admin anteckningar")] = item?.admin_device_notes || "";
        }
      return row;
      });

      const filename = `larm_rapport_${this.customer.name}`;
      this.excelExport(newColumns, "larms", filename);
    },

    deleteAlarmsPrompt() {
      if (this.$refs["alarmTable"].selectedRows.length > 0) {
        this.$modal.show("dialog", {
          title: `<span class="text-red-500">${this.$t('modals.delete_alarms.title', { count: this.$refs["alarmTable"].selectedRows.length })}</span>`,
          buttons: [
            {
              title: `<div class="bg-accent-500 text-white text-sm font-sans py-2">${this.$t("user_actions.cancel")}</div>`,
              handler: () => {
                this.$modal.hide("dialog");
              },
            },
            {
              title: `<div class="bg-red-500 text-white text-sm font-sans py-2">${this.$t("user_actions.delete")}</div>`,
              handler: () => {
                this.deleteSelectedAlarms();
              },
            },
          ],
        });
      }
    },

    async deleteSelectedAlarms() {
      try {
        let alarmIDs = _.map(this.$refs["alarmTable"].selectedRows, "_id");
        let data = {
          alarmIDs: alarmIDs,
        };

        let response = await this.axios.post(`${process.env.VUE_APP_SERVER_URL}/unit/${this.unit_id}/alarms/delete`, data);

        this.$modal.hide("dialog");

        // SEC-364 B3: Safe-4 will not close a subscriber while a case is still
        // running on it, so some of the selection can still be here afterwards.
        const blocked = (response.data && response.data.blocked) || [];
        if (blocked.length > 0) {
          const names = blocked.map((device) => device.unit_name).filter(Boolean).join(", ");
          this.handleError(null, this.$t("modals.delete_alarms.blocked_by_safe4", { names }));
        } else {
          this.handleSuccess(this.$t('modals.delete_alarms.success'));
        }

        this.getAlarms();
      } catch (error) {
        this.$modal.hide("dialog");
        this.handleError(error);
      }
    },

    async downloadPDF(alarm) {
      try {
        let show_alarm_number = false;
        let show_direct_dispatch = false;

        let a = _.find(this.alarm_centrals, { _id: alarm.alarm_central });
        if (a && /(Stanley Security)|(Securitas AB)|(Rapid Larmcentral)|(Cubsec Larmcentral)/.test(a.name)) show_alarm_number = true;
        // Only show direct dispatch for Norwegian site and if alarm central has guard_dispatch enabled
        if (siteContext.isNorwegianSite() && a && a.guard_dispatch === true) show_direct_dispatch = true;
        // SEC-337: the IMSI column, for the one centre that asks for it. This is
        // the same action report as the one downloaded from inside the modal, so
        // it has to print the same columns.
        let show_imsi_number = !!a && a.name === 'Securitas Sverige AB';

        let doc = await this.generateAlarmPlanPDF({ alarm: alarm, customerName: this.customer.name, sellers: this.sellers, oldAlarm: alarm, highlight: false, showAlarmNumber: show_alarm_number, showImsiNumber: show_imsi_number, showDirectDispatch: show_direct_dispatch, unit: _.find(this.units, { unit_id: alarm.parent_id }) || this.unit, customer: this.customer });
        doc.save(`${this.getUnitName(alarm.parent_id)}-${alarm.unit_name}-${alarm.phone}.pdf`);
      } catch (error) {
        this.handleError(error);
      }
    },

    getUnitName(unit_id) {
      let u = _.find(this.units, { unit_id: unit_id });
      if (u && u.name) return u.name;
      else return "-";
    },

    getAlarmLastSeenTime: function(a) {
      return {
        lastSeen: this.moment(a.updatedAt).fromNow(),
        lastSeenTime: this.moment().diff(this.moment(a.updatedAt)),
      };
    },

    getSellerName(id) {
      let s = _.find(this.sellers, { _id: id });
      if (s) return s.name;
      else return "";
    },

    getDeviceStatusLabel(status) {
      const labels = {
        ny_bestallning: this.$t('device_status.ny_bestallning'),
        utbytesenhet: this.$t('device_status.utbytesenhet'),
        ersattningsprodukt: this.$t('device_status.ersattningsprodukt'),
      };
      // Devices with no stored status show an empty cell — do not imply "ny_bestallning"
      return labels[status] || '';
    },


    show(id) {
      this.$modal.show(id);
    },

    showOrderModal() {
      this.$modal.show("modal-order-alarm");
    },

    showAdminOrderModal() {
      this.$modal.show("modal-admin-order-alarm");
    },

    hide(id) {
      this.$modal.hide(id);
    },
    toggleDisabled() {
      this.isDisabled = !this.isDisabled;
    },
    async saveAdminNotes() {
      let data = {
          admin_notes: this.admin_notes
      };
      if (this.unit_id && data) {
        let response = await this.axios.put(`${process.env.VUE_APP_SERVER_URL}/unit/admin-notes/${this.unit_id}`, data);
        if (!response) {
          this.handleError(response.message);
        } else {
          this.toggleDisabled()
        }
      } else return
    },

    // Whether the agreement behind a device's agreement number is insured:
    // true / false when the agreement records it, null when it does not - and
    // null too for an agreement this view knows nothing about (the request was
    // skipped or failed, or the payload carries no insurance field). null means
    // "unknown", which is rendered as a plain uncoloured number.
    agreementInsuranceState(agreementNumber) {
      const insurance = this.agreementInsuranceMap[agreementNumber];
      return insurance === true || insurance === false ? insurance : null;
    },

    getAgreementInsuranceMap() {
      // The /agreement/customer endpoint is not available to the 'user' role
      // (department-level); skip to avoid an unnecessary failing request.
      if (this.user.role == "user") return;
      // Only colours the agreement number column, which white label customers do not have
      if (this.whiteLabelCustomer) return;
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/agreement/customer/${this.customer_id}`)
        .then((response) => {
          const map = {};
          if (response.data.agreements) {
            response.data.agreements.forEach((a) => {
              // Only a recorded insurance state goes into the map. Coercing an
              // absent field to false would paint every such agreement red,
              // which is exactly what "no insurance" is supposed to mean.
              if (a.agreement_number && (a.insurance === true || a.insurance === false)) {
                map[a.agreement_number] = a.insurance;
              }
            });
          }
          this.agreementInsuranceMap = map;
        })
        .catch(() => {
          // Silently fail - agreement data is supplementary
        });
    },
  },

  created() {
    this.getAlarms();
    this.getCustomer();
    this.getAlarmCentrals();
    this.getSellersResellers();
    this.getDevicesTree();
    this.getAgreementInsuranceMap();
  },
  watch: {
    "$i18n.locale"() {
      document.title = this.$t("page_titles.alarm_view");
      this.setPageTitle(`${this.unit.name} - ${this.$t("top_titles.alarm_view")}`, "alarms");
    },
  },
};
</script>
